/**
 * Plan validation and benefit calculation utilities for Change Plan amendment
 */

/**
 * Extract members from policy quotation
 * @param {Object} policy - The policy object
 * @returns {Array} - Array of members with their benefits and premiums
 */
export const extractMembersFromPolicy = (policy) => {
  if (!policy || !policy.quotation || !policy.quotation.policy_schedule_entries) {
    return [];
  }

  return policy.quotation.policy_schedule_entries.map(entry => {
    // Check if entry has a nested dependant structure or flat structure
    if (entry.dependant) {
      // Nested structure (from policy API response)
      return {
        ...entry.dependant,
        benefits: entry.benefits || [],
        premium_amount_in_cents: entry.premium_amount_in_cents || 0,
        uuid: entry.dependant.additional_details && entry.dependant.additional_details.uuid
          ? entry.dependant.additional_details.uuid
          : entry.dependant.guid,
        type: entry.type || (entry.dependant.additional_details && entry.dependant.additional_details.memberType)
      };
    } else {
      // Flat structure (already in correct format)
      return {
        ...entry,
        uuid: entry.uuid || entry.guid
      };
    }
  });
};

/**
 * Sort nuclear members: Principal first, then Spouse, then Children
 * @param {Array} members - Array of nuclear member objects
 * @returns {Array} - Sorted array
 */
const sortNuclearMembers = (members) => {
  const order = { 'Principal': 1, 'Spouse': 2, 'Child': 3 };
  return members.sort((a, b) => {
    return (order[a.relationship] || 999) - (order[b.relationship] || 999);
  });
};

/**
 * Group members by their category
 * @param {Array} members - Array of member objects
 * @returns {Object} - Object with nuclear, parents, and extended arrays
 */
export const groupMembers = (members) => {
  const nuclear = members.filter(m =>
    ['Principal', 'Spouse', 'Child'].includes(m.relationship)
  );

  const parents = members.filter(m =>
    ['Parent', 'Parent_In_Law'].includes(m.relationship)
  );

  const extended = members.filter(m =>
    !['Principal', 'Spouse', 'Child', 'Parent', 'Parent_In_Law'].includes(m.relationship)
  );

  return {
    nuclear: sortNuclearMembers(nuclear),
    parents,
    extended
  };
};

/**
 * Validate parent plan selection
 * @param {String} nuclearPlanCode - Nuclear plan code (e.g., "PLAN_1")
 * @param {String} parentPlanCode - Parent plan code (e.g., "PLAN_1")
 * @returns {Object} - { isValid: boolean, message: string }
 */
export const validateParentPlan = (nuclearPlanCode, parentPlanCode) => {
  if (!nuclearPlanCode || !parentPlanCode) {
    return { isValid: true, message: null };
  }

  // Extract plan numbers (e.g., "PLAN_3" => 3)
  const nuclearPlanNum = parseInt(nuclearPlanCode.split('_')[1]);
  const parentPlanNum = parseInt(parentPlanCode.split('_')[1]);

  if (parentPlanNum > nuclearPlanNum) {
    return {
      isValid: false,
      message: `Parent plan cannot be higher than nuclear plan (Plan ${nuclearPlanNum})`
    };
  }

  return { isValid: true, message: null };
};

/**
 * Get plan number from plan code
 * @param {String} planCode - Plan code (e.g., "PLAN_3")
 * @returns {Number} - Plan number (e.g., 3)
 */
export const getPlanNumber = (planCode) => {
  if (!planCode) return null;
  return parseInt(planCode.split('_')[1]);
};

/**
 * Calculate age from date of birth
 * @param {String} dateOfBirth - Date of birth string
 * @returns {Number} - Age in years
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  const currentYear = new Date().getFullYear();
  const birthYear = new Date(dateOfBirth).getFullYear();
  return currentYear - birthYear;
};

/**
 * Get age-based extended member config from plan
 * @param {Object} plan - Plan object
 * @param {String} dateOfBirth - Date of birth string
 * @returns {Object|null} - Config with premium_rate and benefit_amount, or null
 */
export const getExtendedMemberConfig = (plan, dateOfBirth) => {
  if (!plan || !dateOfBirth) return null;

  const age = calculateAge(dateOfBirth);
  const extendedPremiums = plan.membership_configs &&
    plan.membership_configs.content &&
    plan.membership_configs.content.premiums
    ? plan.membership_configs.content.premiums
    : [];

  const config = extendedPremiums.find((premium) => {
    return age >= premium.entry_age && age <= premium.exit_age;
  });

  return config || null;
};

/**
 * Get member type based on relationship
 * @param {String} relationship - Member relationship
 * @returns {String} - 'Nuclear', 'Extended', or 'N/A'
 */
export const getMemberType = (relationship) => {
  const nuclear = ['Principal', 'Spouse', 'Child'];
  const extended = ['Parent', 'Parent_In_Law'];

  if (nuclear.includes(relationship)) return 'Nuclear';
  if (extended.includes(relationship)) return 'Extended';
  return 'N/A';
};

/**
 * Check if relationship is Extended_2 type (Brother, Nephew, etc.)
 * @param {String} relationship - Member relationship
 * @returns {Boolean} - True if Extended_2 type
 */
export const isExtended2Member = (relationship) => {
  const extended2Relationships = [
    'Uncle', 'Aunt', 'Sister', 'Brother', 'Cousin', 'Nephew', 'Niece',
    'Brother_In_Law', 'Sister_In_Law', 'Grandson', 'Granddaughter',
    'Grandchild', 'Dependant'
  ];
  return extended2Relationships.includes(relationship);
};
